import type { IndexView } from "@nabla/api";
import { useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import { useView, viewOf } from "../../client/index.ts";
import { folded } from "../../design/format/text.ts";
import { phoneQuery, useMatches } from "../../design/media.ts";
import { KeyHint } from "../../design/primitives/KeyHint.tsx";
import { shellCopy } from "../copy.ts";
import { useNavigation } from "../navigation/Navigation.tsx";
import styles from "./TopBar.module.css";

export const index = viewOf("indice")();

type Found = IndexView["objects"][number];
const places = ["campanha", "conjunto", "anuncio"] as const;
const perGroup = 8;

function searchIndex(objects: readonly Found[], query: string): Found[] {
  const words = folded(query).split(/\s+/).filter((word) => word !== "");
  if (words.length === 0) return [];
  const hits = objects.filter((object) => words.every((word) => folded(`${object.name} ${object.metaName ?? ""}`).includes(word)));
  return places.flatMap((place) => hits.filter((hit) => hit.place === place).slice(0, perGroup));
}

export function Search({ focusKey }: { readonly focusKey: number }) {
  const listId = useId();
  const phone = useMatches(phoneQuery);
  const [field, open, list, answered] = useSearch(focusKey, listId);
  return (
    <div className={styles.search} {...(open ? { "data-menu-aberto": "" } : {})}>
      <input
        {...field}
        className={styles.searchInput}
        type="search"
        placeholder={phone ? shellCopy.busca.curto : shellCopy.busca.placeholder}
        aria-label={shellCopy.busca.placeholder}
        role="combobox"
        aria-controls={listId}
        autoComplete="off"
      />
      <span className={styles.searchKey}>
        <KeyHint>{shellCopy.busca.tecla}</KeyHint>
      </span>
      {list ? (
        <div className={styles.results} id={listId} role="listbox" aria-label={shellCopy.busca.placeholder}>
          {list.results.length === 0 ? (
            answered === undefined ? null : <p className={styles.nothing}>{shellCopy.busca.nada(answered)}</p>
          ) : (
            <Groups id={listId} {...list} />
          )}
        </div>
      ) : null}
    </div>
  );
}

interface GroupsProps {
  readonly id: string;
  readonly results: readonly Found[];
  readonly active: number;
  readonly point: (at: number) => void;
  readonly choose: (found: Found) => void;
}

function Groups({ id, results, active, point, choose }: GroupsProps) {
  return places.map((place) => {
    const group = results.filter((found) => found.place === place);
    if (group.length === 0) return null;
    return (
      <div key={place} role="group" aria-label={shellCopy.busca.grupos[place]}>
        <p className={styles.group}>{shellCopy.busca.grupos[place]}</p>
        {group.map((found) => {
          const at = results.indexOf(found);
          return (
            <a
              key={found.href}
              id={`${id}-${at}`}
              role="option"
              aria-selected={at === active}
              className={at === active ? `${styles.result} ${styles.active}` : styles.result}
              href={found.href}
              data-nav="jump"
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => point(at)}
              onClick={(event) => {
                event.preventDefault();
                choose(found);
              }}
            >
              <span className={styles.resultName}>{found.name}</span>
              {found.path.length > 0 ? <span className={styles.resultPath}>{found.path.join(" › ")}</span> : null}
            </a>
          );
        })}
      </div>
    );
  });
}

function useSearch(focusKey: number, listId: string) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [wanted, setWanted] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const navigation = useNavigation();
  const state = useView(wanted ? index : undefined);
  const results = useMemo(() => (state.status === "pronta" ? searchIndex(state.data.objects, query) : []), [state, query]);
  const showing = open && query.trim() !== "";
  useEffect(() => {
    if (focusKey === 0) return;
    input.current?.focus();
    input.current?.select();
  }, [focusKey]);
  const choose = (found: Found | undefined) => {
    if (!found) return;
    setOpen(false);
    setQuery("");
    input.current?.blur();
    navigation.go(found.href, { kind: "jump" });
  };
  const keys = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((at) => (results.length === 0 ? 0 : (at + step + results.length) % results.length));
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(results[active]);
    } else if (event.key === "Escape") {
      event.stopPropagation();
      setOpen(false);
      input.current?.blur();
    }
  };
  const field = {
    ref: input,
    value: query,
    "aria-expanded": showing,
    "aria-activedescendant": showing && results[active] ? `${listId}-${active}` : undefined,
    onFocus: () => {
      setWanted(true);
      setOpen(true);
    },
    onBlur: () => setOpen(false),
    onChange: (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
      setActive(0);
      setOpen(true);
    },
    onKeyDown: keys,
  };
  const list = showing ? { results, active, point: setActive, choose } : undefined;
  return [field, open, list, state.status === "pronta" ? query.trim() : undefined] as const;
}
